import { USE_MOCKS, api, mockDelay } from "@/lib/api"

/**
 * Comentarios: «esto no me cuadra, míralo».
 *
 *   GET  /api/comentarios         ?entity_id&ambito&referencia&anio&mes&estado
 *   GET  /api/comentarios/conteo  ?entity_id&ambito&anio   -> {referencia: n}
 *   POST /api/comentarios
 *   POST /api/comentarios/{id}/resolver   { respuesta }
 *   POST /api/comentarios/{id}/reabrir
 *
 * El diseño —una tabla para los cuatro anclajes, `referencia` como texto y no
 * como clave foránea— está en la migración `0023_comentarios` del backend.
 */

export type AmbitoComentario = "cifra" | "asiento" | "documento" | "factura" | "general"

export interface Comentario {
  id: string
  ambito: AmbitoComentario
  referencia: string
  rotulo: string
  anio: number | null
  mes: number | null
  texto: string
  estado: "abierto" | "resuelto"
  respuesta: string | null
  creado_en: string
  resuelto_en: string | null
  entity_id: string
  sociedad: string
  autor: string | null
  autor_email: string | null
  resuelto_por: string | null
}

export interface Hilo {
  comentarios: Comentario[]
  abiertos: number
  total: number
}

export interface NuevoComentario {
  entity_id: string
  ambito: AmbitoComentario
  referencia?: string
  rotulo?: string
  anio?: number | null
  mes?: number | null
  texto: string
}

/**
 * LA REFERENCIA SE CONSTRUYE AQUÍ Y EN UN SOLO SITIO.
 *
 * Es la cadena que ancla un comentario a lo que se estaba mirando, y tiene que
 * ser LA MISMA la deje quien la deje y la lea quien la lea. Si la compusiera
 * cada pantalla, el P&L escribiría `pnl:Revenue:2026-08` y el desglose de
 * ingresos `revenue:Revenue:2026-8` — y entonces el comentario existe pero la
 * marca no aparece donde hacía falta. No daría ningún error: simplemente nadie
 * vería el aviso.
 *
 * El mes va con dos dígitos por lo mismo: `2026-8` y `2026-08` son dos anclas
 * distintas para la misma celda.
 */
export function refCifra(cuenta: string, anio: number, mes: number): string {
  return `cifra:${cuenta}:${anio}-${String(mes).padStart(2, "0")}`
}

/**
 * Una cuenta de un AÑO, sin mes.
 *
 * Existe aparte de `refCifra` porque no es lo mismo y no puede compartir ancla.
 * Lo que no cuadra suele ser «el costo de ventas de este año», y quien lo mira
 * puede estar en vista mensual, trimestral o anual: anclarlo a un mes haría que
 * el mismo comentario apareciera o desapareciera según la granularidad elegida
 * — un aviso que se esconde solo al cambiar un desplegable.
 *
 * Y NO se resuelve con `refCifra(cuenta, anio, 0)`: eso produce `2026-00`, un
 * mes que no existe, y la bandeja lo mostraría como si fuera un período.
 */
export function refCuenta(cuenta: string, anio: number): string {
  return `cifra:${cuenta}:${anio}`
}

export function refAsiento(asientoId: string): string {
  return `asiento:${asientoId}`
}

export function refDocumento(documentoId: string): string {
  return `documento:${documentoId}`
}

export function refFactura(facturaId: string): string {
  return `factura:${facturaId}`
}

const VACIO: Hilo = { comentarios: [], abiertos: 0, total: 0 }

export async function fetchComentarios(params: {
  entityId?: string | null
  ambito?: AmbitoComentario
  referencia?: string
  anio?: number
  mes?: number
  estado?: "abierto" | "resuelto" | "todos"
}): Promise<Hilo> {
  if (USE_MOCKS) return mockDelay(VACIO)
  return api.get<Hilo>("/comentarios", {
    query: {
      entity_id: params.entityId || undefined,
      ambito: params.ambito,
      referencia: params.referencia,
      anio: params.anio,
      mes: params.mes,
      estado: params.estado ?? "abierto",
    },
  })
}

/**
 * El mapa `{referencia: abiertos}` de una sociedad entera, en UNA llamada.
 *
 * Es lo que evita que una tabla de doce columnas por quince filas haga ciento
 * ochenta peticiones para pintar sus marcas. Ver la cabecera del módulo en el
 * backend.
 */
export async function fetchConteo(params: {
  entityId?: string | null
  ambito?: AmbitoComentario
  anio?: number
}): Promise<{ por_referencia: Record<string, number>; abiertos: number }> {
  if (USE_MOCKS) return mockDelay({ por_referencia: {}, abiertos: 0 })
  return api.get("/comentarios/conteo", {
    query: {
      entity_id: params.entityId || undefined,
      ambito: params.ambito,
      anio: params.anio,
    },
  })
}

export async function crearComentario(c: NuevoComentario) {
  if (USE_MOCKS) return mockDelay({ id: "mock", estado: "abierto" })
  return api.post("/comentarios", c)
}

export async function resolverComentario(id: string, respuesta: string) {
  if (USE_MOCKS) return mockDelay({ id, estado: "resuelto" })
  return api.post(`/comentarios/${id}/resolver`, { respuesta })
}

export async function reabrirComentario(id: string) {
  if (USE_MOCKS) return mockDelay({ id, estado: "abierto" })
  return api.post(`/comentarios/${id}/reabrir`, {})
}
